import React from 'react';
import {
  Box,
  Text,
  Card,
  CardContent,
  DataTable,
  LoadingOrbit,
  Flex,
  TagBadge,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import type { ColumnDef, RowSelectionState, StockFeatures } from '@tanstack/react-table';
import {
  useWorkflowSuitesQuery,
  type BenchmarkRun,
  runStatusVariants,
  runStatusLabels,
  formatDateTime,
} from '../useBenchmarkData';
import { formatDuration, stripBenchmarkPrefix } from './useWorkflowSuiteTransforms';
import { ConfigComparisonView, useConfigDiffBadge } from './config-comparison';

function getSuiteWorkflowNames(run: BenchmarkRun): string[] {
  const meta = run.metadata as Record<string, unknown> | null;
  const names = meta?.workflowNames as string[] | undefined;
  if (names?.length) return names.map(stripBenchmarkPrefix);
  return [];
}

const suiteColumns: ColumnDef<StockFeatures, BenchmarkRun>[] = [
  {
    id: 'suite',
    header: 'Workflow',
    meta: { flex: 2 },
    cell: ({ row }) => {
      const names = getSuiteWorkflowNames(row.original);
      if (names.length === 0) return <Text className="text-muted-foreground">—</Text>;
      return (
        <div className="flex flex-col gap-0.5">
          {names.map((name) => (
            <Text key={name} size="sm" className="truncate" title={name}>
              {name}
            </Text>
          ))}
        </div>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <TagBadge
        name={runStatusLabels[row.original.status]}
        color={badgeVariantToTagColor(runStatusVariants[row.original.status])}
        hideDot
      />
    ),
  },
  {
    accessorKey: 'triggerType',
    header: 'Trigger',
    cell: ({ row }) => <TagBadge name={row.original.triggerType} color="gray" hideDot />,
  },
  {
    id: 'passRate',
    header: 'Pass Rate',
    cell: ({ row }) => {
      const meta = row.original.metadata as Record<string, unknown> | null;
      const total = (meta?.totalWorkflows as number) ?? 0;
      const completed = (meta?.completedWorkflows as number) ?? 0;
      if (total === 0) return <Text className="text-muted-foreground">—</Text>;
      const rate = Math.round((completed / total) * 100);
      return (
        <Text
          className={
            rate === 100 ? 'text-success' : rate === 0 ? 'text-destructive' : 'text-warning'
          }
        >
          {completed}/{total} ({rate}%)
        </Text>
      );
    },
  },
  {
    id: 'duration',
    header: 'Duration',
    cell: ({ row }) => {
      const start = row.original.startedAt;
      const end = row.original.completedAt;
      if (!start || !end) return <Text className="text-muted-foreground">—</Text>;
      const ms = new Date(end).getTime() - new Date(start).getTime();
      return <Text>{formatDuration(ms)}</Text>;
    },
  },
  {
    accessorKey: 'startedAt',
    header: 'Started',
    cell: ({ row }) => (
      <Text size="sm" className="text-muted-foreground">
        {formatDateTime(row.original.startedAt)}
      </Text>
    ),
  },
];

interface WorkflowSuiteRunsTableProps {
  onSelectSuite: (suiteId: string) => void;
}

export function WorkflowSuiteRunsTable({ onSelectSuite }: WorkflowSuiteRunsTableProps) {
  const [page, setPage] = React.useState(1);
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [showComparison, setShowComparison] = React.useState(false);
  const query = useWorkflowSuitesQuery({ page, pageSize: 10 });

  const data = query.data?.data ?? [];

  // Get selected runs (max 2), sorted chronologically (older first)
  const selectedRuns = React.useMemo(() => {
    const indices = Object.keys(rowSelection)
      .filter((k) => rowSelection[k])
      .map(Number);
    const runs = indices.map((i) => data[i]).filter(Boolean);
    return runs.sort(
      (a, b) => new Date(a.startedAt ?? 0).getTime() - new Date(b.startedAt ?? 0).getTime()
    );
  }, [rowSelection, data]);

  const runA = selectedRuns[0] ?? null;
  const runB = selectedRuns[1] ?? null;
  const diffSummary = useConfigDiffBadge(runA, runB);
  const hasTwoSelected = selectedRuns.length === 2;

  // Limit selection to max 2 rows — when a third is selected, drop the oldest
  const handleRowSelectionChange = React.useCallback(
    (updaterOrValue: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => {
      setRowSelection((prev) => {
        const next = typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue;
        const selected = Object.keys(next).filter((k) => next[k]);
        if (selected.length <= 2) return next;

        const prevSelected = new Set(Object.keys(prev).filter((k) => prev[k]));
        const newKey = selected.find((k) => !prevSelected.has(k));
        const kept = [...prevSelected].pop();
        const limited: RowSelectionState = {};
        if (kept) limited[kept] = true;
        if (newKey) limited[newKey] = true;
        return limited;
      });
    },
    []
  );

  if (query.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-10">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  }

  if (!data.length) {
    return (
      <Text className="text-muted-foreground py-6 text-center">
        No workflow suite runs yet. Trigger a run to get started.
      </Text>
    );
  }

  return (
    <Box className="space-y-4">
      {/* Comparison section */}
      {hasTwoSelected && (
        <Card padding="md">
          <CardContent>
            <button
              onClick={() => setShowComparison(!showComparison)}
              className="flex items-center gap-2 w-full text-left"
            >
              {showComparison ? <CaretDown size={14} /> : <CaretRight size={14} />}
              <Text weight="medium">Configuration Comparison</Text>
              {diffSummary?.hasChanges === true && (
                <TagBadge name="Config differs" color="yellow" hideDot className="text-xs" />
              )}
              {diffSummary?.hasChanges === false && (
                <TagBadge name="Config identical" color="blue" hideDot className="text-xs" />
              )}
              <Text size="xs" className="text-muted-foreground ml-auto">
                {formatDateTime(runA!.startedAt)} vs {formatDateTime(runB!.startedAt)}
              </Text>
            </button>
            {showComparison && (
              <Box className="mt-4">
                <ConfigComparisonView
                  runA={runA!}
                  runB={runB!}
                  summary={diffSummary ?? undefined}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Hint when 1 selected */}
      {selectedRuns.length === 1 && (
        <Text size="sm" className="text-muted-foreground">
          Select one more run to compare configurations
        </Text>
      )}

      {/* Table */}
      <DataTable
        columns={suiteColumns}
        data={data}
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
        onRowClick={(row) => onSelectSuite(row.id)}
        serverPagination={{
          page: query.data!.pagination.page,
          pageSize: query.data!.pagination.pageSize,
          pageCount: query.data!.pagination.totalPages,
          totalItems: query.data!.pagination.totalItems,
          onPageChange: setPage,
        }}
      />
    </Box>
  );
}
